/**
 * De Smokkelroute (README §5.8): drie vragen, zes bieren, één knop naar de
 * winkelwagen. De bieren komen als JSON uit de sectie. De score komt uit het
 * prototype, maar de stijl weegt nu zwaarder dan alcohol en herkomst samen
 * (feitencheck 2026: anders won pils bij "Hoppig" en stout bij "Fris").
 *
 * "Wissel dit bier" vervangt alleen dát bier door de best scorende kandidaat die
 * nog niet in de zes zit en nog niet gewisseld is (onderzoek ronde 2, B20); de
 * andere vijf blijven staan. "Pas zelf aan" zet de zes in sessionStorage in het
 * schema van smokkelaar-sixpack.js en opent de sixpack-stand voorgevuld.
 * Geen state buiten dit element, dus opnieuw laden begint gewoon bij vraag 1.
 */

import { voegToe, heeftLade, naarWinkelwagen, regels, sixpackKortingCent } from '@theme/smokkelaar-cart';

const VRAGEN = [
  {
    vraag: 'Waar ga je normaal voor?',
    opties: [
      ['fris', 'Fris en licht', 'Pils, blond, wit, weizen, sour'],
      ['bitter', 'Hoppig en bitter', 'IPA, pale ale'],
      // Geen "donker": tripel is blond.
      ['vol', 'Vol en stevig', 'Bok, stout, tripel'],
    ],
  },
  {
    vraag: 'Hoe zwaar mag het zijn?',
    opties: [
      ['licht', 'Licht, tot en met 5%', 'Voor een avond met meer dan één'],
      ['zwaar', 'Mag zwaarder', 'Ook boven de 5%'],
    ],
  },
  {
    vraag: 'Bekend terrein of avontuur?',
    opties: [
      ['bekend', 'Houd het bekend', 'Vooral ons eigen bier en Twentse brouwers'],
      // De score beloont alleen herkomst Duitsland en België; meer belooft de knop niet.
      ['avontuur', 'Verras me', 'Ook bier uit Duitsland en België'],
    ],
  },
];

// Stijlgroepen per antwoord op vraag 1.
const STIJLEN = { fris: ['pils', 'blond', 'weizen', 'sour'], bitter: ['ipa'], vol: ['bok', 'stout', 'tripel'] };
const DICHTBIJ = ['Overdinkel', 'Twente'];
const OVER_DE_GRENS = ['Duitsland', 'België'];
// Eén grens voor "licht" op de hele site: t/m 5,0%, net als de filterknop "Licht" (smokkelaar-schuif.liquid).
const LICHT = 5;
// En "stevig" vanaf 7%, net als de filterknop "Stevig".
const STEVIG = 7;

/**
 * Stijl telt 10: meer dan alcohol (±2), herkomst (+2/−1 of +1,5) en smaak (≤ 1)
 * samen. Zo komen de zes eerst uit de gekozen stijlen en vult de rest alleen
 * aan als die stijlen te weinig bieren hebben.
 */
function score(b, [drink, gewicht, avontuur]) {
  let s = STIJLEN[drink].includes(b.stijl) ? 10 : 0;
  if (drink === 'bitter') s += b.bitter / 100;
  if (drink === 'vol') s += b.vol / 100;
  s += gewicht === 'licht' ? (b.abv <= LICHT ? 2 : -2) : b.abv > LICHT ? 1 : 0;
  s += avontuur === 'bekend'
    ? (DICHTBIJ.includes(b.herkomst) ? 2 : -1)
    : (OVER_DE_GRENS.includes(b.herkomst) ? 1.5 : 0);
  return s;
}

function waarom(b, drink) {
  // Een aanvuller buiten de gekozen stijlen is niet "zoals gevraagd" (QA lijst C).
  if (!STIJLEN[drink].includes(b.stijl)) return 'Ter afwisseling';
  if (drink === 'bitter') return 'Hop voorop';
  // Alleen over de body, niet over kleur: tripel is blond, rauchbier amber.
  // Vanaf 60 is "veel", net als het filter "Vol & stevig" (smokkelaar-filterkop.liquid).
  if (drink === 'vol') return b.vol >= 60 ? 'Vol en stevig' : 'Iets minder vol';
  // Oud bruin is zuur maar moutig en donker; een volle body is hier de maat.
  if (b.stijl === 'sour') return b.vol >= 50 ? 'Zuur, met donker fruit' : 'Frisse uitschieter';
  return 'Licht en fris, zoals gevraagd';
}

/**
 * De zin boven de zes. Hij telt echt: heeft een stijl te weinig lichte bieren
 * of te weinig van dichtbij, dan zegt hij dat in plaats van "bijna allemaal".
 */
function uitleg(picks, [, gewicht, avontuur]) {
  const n = picks.length;
  const tel = (past) => picks.filter(past).length;
  const dichtbij = tel((b) => DICHTBIJ.includes(b.herkomst));
  const over = tel((b) => OVER_DE_GRENS.includes(b.herkomst));
  const licht = tel((b) => b.abv <= LICHT);
  const stevig = tel((b) => b.abv >= STEVIG);

  const herkomst = avontuur === 'avontuur'
    ? (over === 0 ? 'Over de grens hebben we in deze smaak nu niets.'
      : over * 2 > n ? 'Vooral van over de grens.'
      : over === 1 ? 'Met één bier van over de grens.'
      : 'Met een paar bieren van over de grens.')
    : (dichtbij * 2 > n ? 'Vooral van dichtbij.'
      : dichtbij === 0 ? 'Van dichtbij hebben we in deze smaak nu niets.'
      : `${dichtbij} van de ${n} komen van dichtbij; meer hebben we in deze smaak nu niet.`);

  const alcohol = gewicht === 'licht'
    ? (licht === n ? 'Allemaal tot en met 5%, dus je kunt er meer dan één.'
      // "Bijna allemaal" pas vanaf 5 van de 6.
      : licht * 6 >= n * 5 ? 'Bijna allemaal tot en met 5%, dus je kunt er meer dan één.'
      : licht > 0 ? `${licht} van de ${n} zijn tot en met 5%; lichter hebben we in deze smaak nu niet.`
      : 'Tot en met 5% hebben we in deze smaak nu niets. Neem de tijd.')
    : (stevig === 0 ? 'Zwaarder mocht, maar geen van deze haalt de 7%.'
      : stevig * 2 > n ? 'Vooral stevig bier, dus rustig aan.'
      : stevig === 1 ? 'Eén stevige erbij, voor het einde van de avond.'
      : 'Een paar stevige erbij, voor het einde van de avond.');

  return `${herkomst} ${alcohol}`;
}

// Zelfde fles als snippets/smokkelaar-fles.liquid, voor een bier zonder foto; de
// kleur komt uit --stijlkleur op de link eromheen (QA lijst C: was een leeg vakje).
const FLES = '<svg class="smokkelaar-route__fles" viewBox="0 0 48 120" aria-hidden="true" focusable="false"><path d="M18 0h12a1.5 1.5 0 0 1 1.5 1.5V8h-15V1.5A1.5 1.5 0 0 1 18 0z"/><path d="M19 8h10v24c0 6 11 10 11 20v58a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V52c0-10 11-14 11-20z"/></svg>';

// Aantal en korting uit de thema-instellingen (smokkelaar-cart.js); statiegeld per bier uit de JSON.
const AANTAL = regels.sixpackAantal;
const SIXPACK_SLEUTEL = 'smokkelaar-sixpack';

const eur = (cent) => '€ ' + (cent / 100).toFixed(2).replace('.', ',');
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const abv = (n) => Number(n).toFixed(1).replace('.', ',') + '%';
const root = () => window.Shopify?.routes?.root || '/';

class SmokkelaarSmokkelroute extends HTMLElement {
  #antwoorden = [];
  #bieren = [];
  #picks = [];
  #uitgesloten = new Set();
  #scherm;

  connectedCallback() {
    const bron = this.querySelector('[data-bieren]');
    try {
      this.#bieren = JSON.parse(bron?.textContent || '[]').filter((b) => b.stijl && b.beschikbaar);
    } catch {
      this.#bieren = [];
    }
    this.#scherm = this.querySelector('[data-scherm]');
    this.addEventListener('click', (e) => this.#klik(e));
    this.#render();
  }

  #pool() {
    return [...this.#bieren].sort((a, b) => score(b, this.#antwoorden) - score(a, this.#antwoorden));
  }

  #kies() {
    this.#uitgesloten.clear();
    this.#picks = this.#pool().slice(0, AANTAL);
  }

  /** Vervang alleen het bier op plek i; de rest blijft staan. */
  #wissel(i) {
    const oud = this.#picks[i];
    if (!oud) return;
    const inZes = new Set(this.#picks.map((b) => b.id));
    const pool = this.#pool();
    let nieuw = pool.find((b) => !inZes.has(b.id) && !this.#uitgesloten.has(b.id));
    if (!nieuw) {
      // Alles al een keer gezien: de gewisselde bieren mogen weer terugkomen.
      this.#uitgesloten.clear();
      nieuw = pool.find((b) => !inZes.has(b.id));
    }
    if (!nieuw) return;
    this.#uitgesloten.add(oud.id);
    this.#picks[i] = nieuw;
  }

  #klik(e) {
    const t = e.target.closest('[data-actie]');
    if (!t) return;
    const actie = t.dataset.actie;
    if (actie === 'zelf') {
      // Gewone link; alleen de zes klaarzetten voor de sixpack-stand.
      this.#vulSixpack();
      return;
    }
    if (actie === 'kies') {
      this.#antwoorden.push(t.dataset.waarde);
      if (this.#antwoorden.length === VRAGEN.length) this.#kies();
    } else if (actie === 'terug') {
      this.#antwoorden.pop();
    } else if (actie === 'wissel') {
      const i = Number(t.dataset.index);
      this.#wissel(i);
      this.#render();
      // Focus blijft op dezelfde rij, zodat je door kunt wisselen.
      this.#scherm.querySelectorAll('[data-actie="wissel"]')[i]?.focus();
      return;
    } else if (actie === 'opnieuw') {
      this.#antwoorden = [];
      this.#picks = [];
      this.#uitgesloten.clear();
    } else if (actie === 'winkelwagen') {
      this.#naarWinkelwagen(t);
      return;
    }
    this.#render();
    this.#scherm.querySelector('h2, button')?.focus();
  }

  #vulSixpack() {
    try {
      sessionStorage.setItem(
        SIXPACK_SLEUTEL,
        JSON.stringify(
          this.#picks.map((b) => ({ variant: String(b.id), prijs: b.prijs, naam: b.naam, beeld: b.beeld || '', kleur: b.kleur || '', voorraad: b.voorraad ?? 99, statiegeld: b.statiegeld || 0 }))
        )
      );
    } catch {
      /* privémodus: dan begint de bouwer leeg */
    }
  }

  #fout(tekst) {
    let el = this.#scherm.querySelector('[data-fout]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'smokkelaar-route__fout';
      el.setAttribute('data-fout', '');
      el.setAttribute('role', 'alert');
      this.#scherm.querySelector('.smokkelaar-route__totaal')?.insertAdjacentElement('afterend', el);
    }
    el.textContent = tekst;
  }

  /** Welke van de zes zitten al maximaal in de wagen? (Shopify weigert de hele toevoeging.) */
  async #opInWagen() {
    try {
      const wagen = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const inWagen = new Map();
      for (const i of wagen.items) inWagen.set(i.variant_id, (inWagen.get(i.variant_id) || 0) + i.quantity);
      return this.#picks.filter((b) => (inWagen.get(b.id) || 0) + 1 > (b.voorraad ?? 99));
    } catch {
      return [];
    }
  }

  async #naarWinkelwagen(knop) {
    knop.disabled = true;
    this.#scherm.querySelectorAll('.is-op').forEach((li) => li.classList.remove('is-op'));
    try {
      await voegToe(this.#picks.map((b) => ({ id: b.id, quantity: 1 })), { bron: knop });
      if (!heeftLade()) {
        naarWinkelwagen();
        return;
      }
      // De lade gaat vanzelf open; sluit je die, dan staat hier de volgende stap.
      const vervolg = document.createElement('div');
      vervolg.className = 'smokkelaar-route__vervolg';
      vervolg.innerHTML = `<a class="button" href="${root()}checkout">Naar afrekenen</a><a class="button button-secondary" href="${this.dataset.cart || `${root()}cart`}">Bekijk winkelwagen</a>`;
      knop.replaceWith(vervolg);
      const klaar = document.createElement('p');
      klaar.className = 'smokkelaar-route__klaar';
      klaar.textContent = `Je sixpack staat in je winkelwagen, met ${regels.sixpackKorting}% sixpackkorting.`;
      this.#scherm.querySelector('.smokkelaar-route__totaal')?.insertAdjacentElement('afterend', klaar);
    } catch (err) {
      console.error('Smokkelroute: toevoegen mislukt', err);
      knop.disabled = false;
      const op = await this.#opInWagen();
      if (op.length) {
        const rijen = this.#scherm.querySelectorAll('.smokkelaar-route__bier');
        op.forEach((b) => rijen[this.#picks.indexOf(b)]?.classList.add('is-op'));
        const namen = op.map((b) => b.naam).join(', ');
        this.#fout(
          op.length === 1
            ? `Van ${namen} zit het maximum al in je winkelwagen. Wissel dat bier en probeer het nog eens.`
            : `Van ${namen} zit het maximum al in je winkelwagen. Wissel die bieren en probeer het nog eens.`
        );
      } else {
        this.#fout(`${err?.message ? err.message + ' ' : ''}Probeer het nog eens, of kies zelf uit alle bieren.`);
      }
    }
  }

  #render() {
    const stap = this.#antwoorden.length;
    const resultaat = stap >= VRAGEN.length;
    this.classList.toggle('is-resultaat', resultaat);
    this.querySelectorAll('.smokkelaar-route__voortgang span').forEach((s, i) => s.classList.toggle('is-af', i < stap));
    this.#scherm.innerHTML = resultaat ? this.#resultaatHtml() : this.#vraagHtml(stap);
  }

  #vraagHtml(i) {
    const v = VRAGEN[i];
    return `
      <h2 class="smokkelaar-route__vraag" tabindex="-1">${esc(v.vraag)}</h2>
      <div class="smokkelaar-route__opties">
        ${v.opties.map(([waarde, titel, sub]) => `
          <button type="button" class="smokkelaar-route__optie" data-actie="kies" data-waarde="${waarde}">
            <strong>${esc(titel)}</strong>
            <span>${esc(sub)}</span>
          </button>`).join('')}
      </div>
      ${i > 0 ? '<button type="button" class="smokkelaar-route__tekstknop" data-actie="terug">← Vorige vraag</button>' : ''}`;
  }

  #resultaatHtml() {
    const [drink] = this.#antwoorden;
    const picks = this.#picks;
    const los = picks.reduce((s, b) => s + b.prijs, 0);
    const korting = sixpackKortingCent(picks.map((b) => b.prijs));
    const statiegeld = picks.reduce((s, b) => s + (b.statiegeld || 0), 0);
    const titel = drink === 'fris' ? 'De Grensfrisse' : drink === 'bitter' ? 'De Hopsmokkel' : 'De Nachtroute';

    if (!picks.length) {
      return '<p>Er is nu even geen bier op voorraad om uit te kiezen.</p>';
    }

    return `
      <p class="smokkelaar-kicker">Jouw route</p>
      <h2 class="smokkelaar-route__titel" tabindex="-1">${titel}</h2>
      <p class="smokkelaar-route__uitleg">${uitleg(picks, this.#antwoorden)}</p>
      <ul class="smokkelaar-route__lijst">
        ${picks.map((b, i) => `
          <li class="smokkelaar-route__bier">
            <a class="smokkelaar-route__beeld" href="${esc(b.url)}" style="--stijlkleur: ${esc(b.kleur)};">
              ${b.beeld ? `<img src="${esc(b.beeld)}" alt="" loading="lazy" width="120" height="120">` : FLES}
              <span class="smokkelaar-route__stip"></span>
            </a>
            <div class="smokkelaar-route__biertekst">
              <p class="smokkelaar-route__meta">${esc(b.brouwerij || '')}${b.brouwerij ? ' · ' : ''}${abv(b.abv)}</p>
              <p class="smokkelaar-route__naam"><a href="${esc(b.url)}">${esc(b.naam)}</a></p>
              <p class="smokkelaar-route__waarom">${waarom(b, drink)}</p>
            </div>
            <div class="smokkelaar-route__bierrechts">
              <span>${eur(b.prijs)}</span>
              <button type="button" class="smokkelaar-route__tekstknop" data-actie="wissel" data-index="${i}" title="Wissel voor een ander bier">Wissel dit bier</button>
            </div>
          </li>`).join('')}
      </ul>
      <div class="smokkelaar-route__totaal">
        <div>
          <s>${eur(los)}</s>
          <strong>${eur(los - korting)}</strong>
          <span>sixpackkorting −${regels.sixpackKorting}%</span>
          ${statiegeld > 0 ? `<small class="smokkelaar-route__statiegeld">+ ${eur(statiegeld)} statiegeld</small>` : ''}
        </div>
        <button type="button" class="button" data-actie="winkelwagen">Zet in winkelwagen</button>
      </div>
      <div class="smokkelaar-route__onder">
        <a class="smokkelaar-route__tekstknop" href="${esc(this.dataset.catalogus)}" data-actie="zelf">Pas zelf aan bij alle bieren →</a>
        <button type="button" class="smokkelaar-route__tekstknop" data-actie="opnieuw">Begin opnieuw</button>
      </div>`;
  }
}

if (!customElements.get('smokkelaar-smokkelroute')) {
  customElements.define('smokkelaar-smokkelroute', SmokkelaarSmokkelroute);
}
